<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    scrollable
    content-class="po-detail-dialog"
    @click:outside="close"
  >
    <v-card class="po-detail-card">
      <v-card-text class="pa-0 po-detail-dialog__body">
        <div v-if="loading" class="d-flex justify-center align-center py-12">
          <v-progress-circular indeterminate color="secondary"></v-progress-circular>
        </div>

        <template v-else-if="detail">
          <section class="po-detail-section px-6 pt-5 pb-4">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3 class="po-detail-section__title mb-0">
                {{ purchaseOrderLabel }}
              </h3>
              <v-btn
                fab
                x-small
                depressed
                color="primary"
                class="po-detail-section__edit-btn white--text"
                @click="onEdit"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>

            <div
              v-for="(row, rowIndex) in fieldRows"
              :key="`po-row-${rowIndex}`"
              class="po-detail-grid-row"
              :class="{ 'po-detail-grid-row--spaced': rowIndex > 0 }"
            >
              <div
                v-for="field in row"
                :key="field.key"
                class="po-detail-grid-cell"
                :class="{ 'po-detail-grid-cell--full': field.fullWidth }"
              >
                <div class="field-label">{{ field.label }}</div>
                <div class="field-value">{{ field.value }}</div>
              </div>
            </div>
          </section>

          <section class="po-detail-section px-6 py-4">
            <h3 class="po-detail-plain-title mb-3">
              {{ $t("memberDataSection.purchaseOrderDetail.photos") }}
            </h3>
            <div class="po-detail-media-row">
              <template v-if="photoUrls.length">
                <v-img
                  v-for="(url, index) in photoUrls"
                  :key="`po-photo-${index}`"
                  :src="url"
                  width="120"
                  height="120"
                  class="po-detail-photo rounded"
                  cover
                ></v-img>
              </template>
              <div v-else class="po-detail-photo-placeholder rounded" aria-hidden="true">
                <v-icon size="40" color="#bdbdbd">mdi-image-outline</v-icon>
              </div>
            </div>
          </section>

          <section class="po-detail-section px-6 py-4">
            <h3 class="po-detail-plain-title mb-3">
              {{ $t("memberDataSection.greenCoffee.qrCode") }}
            </h3>
            <div class="po-detail-qr-row">
              <div class="po-detail-qr-group">
                <div class="po-detail-qr-block">
                  <div class="po-detail-qr-placeholder">
                    <qrcode
                      v-if="detail.qrCodes.internalQrText"
                      :text="detail.qrCodes.internalQrText"
                      :size="108"
                      class="po-detail-qr-code"
                    ></qrcode>
                    <v-icon v-else size="48" color="#bdbdbd">mdi-qrcode</v-icon>
                  </div>
                  <p class="po-detail-qr-id-line mb-0">
                    <span class="po-detail-qr-id-line__text">
                      {{ $t("memberDataSection.parchmentDelivery.bulkImport.internalId") }}:
                      <template v-if="detail.qrCodes.internalId">{{ detail.qrCodes.internalId }}</template>
                      <span v-else class="po-detail-qr-id-missing">—</span>
                    </span>
                  </p>
                </div>
                <div class="po-detail-qr-block">
                  <div class="po-detail-qr-placeholder">
                    <qrcode
                      v-if="detail.qrCodes.externalQrText"
                      :text="detail.qrCodes.externalQrText"
                      :size="108"
                      class="po-detail-qr-code"
                    ></qrcode>
                    <v-icon v-else size="48" color="#bdbdbd">mdi-qrcode</v-icon>
                  </div>
                  <p class="po-detail-qr-id-line mb-0">
                    <span class="po-detail-qr-id-line__text">
                      {{ $t("memberDataSection.greenCoffee.externalId") }}:
                      <template v-if="detail.qrCodes.externalId">{{ detail.qrCodes.externalId }}</template>
                      <span v-else class="po-detail-qr-id-missing">—</span>
                    </span>
                  </p>
                </div>
              </div>
              <v-btn
                outlined
                color="primary"
                height="36"
                class="po-detail-qr-download-btn text-uppercase px-5"
                @click="downloadQrCodes"
              >
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.downloadQr") }}
              </v-btn>
            </div>
          </section>

          <section class="po-detail-section px-6 pt-0 pb-4">
            <v-divider class="po-detail-section-divider mb-4"></v-divider>
            <div class="po-detail-actions">
              <v-btn
                color="primary"
                height="36"
                depressed
                class="text-uppercase white--text px-5 mr-3 mb-2"
                :loading="generatingReport"
                :disabled="generatingReport || !purchaseOrder"
                @click="downloadReport"
              >
                {{ $t("memberDataSection.purchaseOrderDetail.downloadReport") }}
              </v-btn>
              <v-btn
                outlined
                color="primary"
                height="36"
                class="text-uppercase px-5 mb-2"
                @click="onQualityControl"
              >
                <v-icon left small>mdi-seal</v-icon>
                {{ $t("memberDataSection.greenCoffee.qualityControlButton") }}
              </v-btn>
            </div>
          </section>
        </template>
      </v-card-text>

      <v-divider v-if="!loading && purchaseOrder" class="po-detail-section-divider"></v-divider>
      <v-card-actions class="px-6 py-4 po-detail-footer">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          height="36"
          class="text-uppercase px-5 mr-3"
          @click="onEdit"
        >
          {{ $t("edit") }}
        </v-btn>
        <v-btn
          color="primary"
          height="36"
          depressed
          class="text-uppercase white--text px-5"
          @click="close"
        >
          {{ $t("memberDataSection.greenCoffee.close") }}
        </v-btn>
      </v-card-actions>
      <PurchaseOrderReportPdfGenerator ref="reportPdfGenerator" />
    </v-card>

    <PurchaseOrderQualityGradingDialog
      v-model="qualityGradingVisible"
      :total-quantity="qualityGradingTotalQuantity"
      :unit-size="40"
      :lots="qualityGradingLots"
      :quality-scores="gradingQualityScores"
      :labels="gradingLabels"
      :grade-options="gradingGradeOptions"
      @finish-grading="onQualityGradingFinished"
      @lots-changed="qualityGradingLots = $event"
    />
  </v-dialog>
</template>

<script>
import { isKenyaClient } from "@/utils";
import moment from "moment";
import BuyingStationService from "@/_services/BuyingStationService";
import PurchaseOrderReportPdfGenerator from "@/components/reports/PurchaseOrderReportPdfGenerator.vue";
import PurchaseOrderQualityGradingDialog from "./QualityGradingDialog.vue";

const DEFAULT_GRADING_GRADE_OPTIONS = [
  { text: "A", value: "A" },
  { text: "AA", value: "AA" },
  { text: "B", value: "B" },
  { text: "C", value: "C" },
];

/** Layout preview only — set to null before release */
const PO_DETAIL_EXTERNAL_ID_PREVIEW = "EX-000956";

export default {
  name: "PurchaseOrderDetailDialog",
  components: {
    PurchaseOrderReportPdfGenerator,
    PurchaseOrderQualityGradingDialog,
  },
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      generatingReport: false,
      purchaseOrder: null,
      qualityGradingVisible: false,
      qualityGradingLots: [],
      gradingQualityScores: [],
      gradingLabels: [],
      gradingGradeOptions: [...DEFAULT_GRADING_GRADE_OPTIONS],
    };
  },
  computed: {
    purchaseOrderLabel() {
      return isKenyaClient() ? this.$t("memberDataSection.cherryCollection") : this.$t("memberDataSection.purchaseOrderDetail.sectionTitle");
    },
    qualityGradingTotalQuantity() {
      const qty = this.purchaseOrder?.coffeeCherryQty;
      return Number(qty) || 0;
    },
    baseI18n() {
      return "memberDataSection";
    },
    detail() {
      if (!this.purchaseOrder) return null;
      const po = this.purchaseOrder;
      const farmerName = [po.farmer?.firstName, po.farmer?.middleName, po.farmer?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const varietyName =
        po.coffeeVariety?.name ||
        po.coffeeVarietyDirect?.name ||
        (Array.isArray(po.coffeeVariety)
          ? po.coffeeVariety.map((v) => v.name).filter(Boolean).join(", ")
          : "") ||
        this.record?.coffeeVarietyDirect?.name ||
        (Array.isArray(this.record?.coffeeVariety)
          ? this.record.coffeeVariety.map((v) => v.name).join(", ")
          : "");
      const coffeeTypeName =
        po.coffeeType?.name || this.record?.coffeeType?.name || "—";
      const qty =
        po.coffeeCherryQty != null && po.coffeeCherryQty !== ""
          ? `${po.coffeeCherryQty} kg`
          : "—";

      return {
        lotId: po.orderCode || "—",
        purchaseDate: this.formatPurchaseDate(po.purchasedAt),
        farm: po.userFarms?.farmName || "—",
        farmer: farmerName || "—",
        plantation: po.plantations?.plantation_name || "—",
        coffeeSpecies: po.coffeeSpecies?.name || "—",
        coffeeVariety: varietyName || "—",
        coffeeType: coffeeTypeName,
        coffeeCherryQty: qty,
        coffeeCherryQlty: po.coffeeCherryQlty || "—",
        qrCodes: (() => {
          const internalId = this.resolveInternalId(po);
          const externalId =
            this.resolveExternalId(po) || PO_DETAIL_EXTERNAL_ID_PREVIEW || null;
          return {
            internalId,
            externalId,
            internalQrText: internalId || "",
            externalQrText: externalId || "",
          };
        })(),
      };
    },
    fieldRows() {
      if (!this.detail) return [];
      const t = (key) => this.$t(`${this.baseI18n}.${key}`);
      const d = this.detail;
      return [
        [
          { key: "lotId", label: this.$t("memberDataSection.purchaseOrderDetail.lotId"), value: d.lotId },
          { key: "purchaseDate", label: t("purchaseDate"), value: d.purchaseDate },
          { key: "farm", label: t("farm"), value: d.farm },
        ],
        [
          { key: "farmer", label: t("farmer"), value: d.farmer },
          { key: "plantation", label: t("plantation"), value: d.plantation },
          { key: "coffeeSpecies", label: t("coffeeSpecies"), value: d.coffeeSpecies },
        ],
        [
          { key: "coffeeVariety", label: t("coffeeVariety"), value: d.coffeeVariety },
          { key: "coffeeType", label: t("coffeeType"), value: d.coffeeType },
          { key: "coffeeCherryQty", label: t("coffeeCherryQuantity"), value: d.coffeeCherryQty },
        ],
        [
          {
            key: "coffeeCherryQlty",
            label: t("coffeeCherryQuality"),
            value: d.coffeeCherryQlty,
            fullWidth: true,
          },
        ],
      ];
    },
    photoUrls() {
      const pic = this.purchaseOrder?.coffeeCherryPic;
      if (!pic) return [];
      const urls = Array.isArray(pic) ? pic : [pic];
      return urls
        .map((item) => this.resolvePhotoUrl(item))
        .filter(Boolean);
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        this.loadPurchaseOrder();
      }
    },
    visible(v) {
      this.$emit("input", v);
      if (!v) {
        this.purchaseOrder = null;
      }
    },
    record: {
      handler() {
        if (this.visible) {
          this.loadPurchaseOrder();
        }
      },
      deep: true,
    },
  },
  methods: {
    async loadPurchaseOrder() {
      const orderCode = this.record?.orderCode || this.record?.id;
      if (!orderCode) {
        this.purchaseOrder = this.mergePurchaseOrderWithRecord(this.record);
        return;
      }
      this.loading = true;
      try {
        const data = await BuyingStationService.getPurchaseOrder(orderCode);
        if (data.code === 200) {
          this.purchaseOrder = this.mergePurchaseOrderWithRecord(data.data);
          this.qualityGradingLots = Array.isArray(data.data.qualityGradingLots)
            ? [...data.data.qualityGradingLots]
            : [];
        } else {
          this.purchaseOrder = this.mergePurchaseOrderWithRecord(this.record);
        }
      } catch (err) {
        this.purchaseOrder = this.mergePurchaseOrderWithRecord(this.record);
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      } finally {
        this.loading = false;
      }
    },
    formatPurchaseDate(value) {
      if (!value) return "—";
      const parsed = moment(value, [moment.ISO_8601, "MM/DD/YYYY", "DD-MMM-YYYY"], true);
      if (parsed.isValid()) {
        return parsed.format("DD-MMM-YYYY").toUpperCase();
      }
      return String(value).toUpperCase();
    },
    resolvePhotoUrl(item) {
      if (!item) return null;
      if (typeof item === "string") return item;
      return item.file_name || item.location || item.s3_key || null;
    },
    normalizeId(value) {
      const id = value != null ? String(value).trim() : "";
      return id || null;
    },
    resolveInternalId(po) {
      if (!po) return null;
      return (
        this.normalizeId(po.internalId) ||
        this.normalizeId(po.internal_id) ||
        this.normalizeId(po.recordId) ||
        this.normalizeId(po.orderCode) ||
        this.normalizeId(this.record?.internalId) ||
        this.normalizeId(this.record?.internal_id) ||
        this.normalizeId(this.record?.recordId) ||
        this.normalizeId(this.record?.orderCode) ||
        this.parseQrField(po.qrCodeDataString, [
          "orderCode",
          "internalId",
          "internal_id",
          "recordId",
        ])
      );
    },
    resolveExternalId(po) {
      if (!po) return null;
      return (
        this.normalizeId(po.external_id) ||
        this.normalizeId(po.externalId) ||
        this.normalizeId(this.record?.external_id) ||
        this.normalizeId(this.record?.externalId) ||
        this.parseQrField(po.qrCodeDataString, ["external_id", "externalId"])
      );
    },
    parseQrField(qrCodeDataString, fields = []) {
      if (!qrCodeDataString || !fields.length) return null;
      try {
        const parsed = JSON.parse(qrCodeDataString);
        for (let i = 0; i < fields.length; i += 1) {
          const value = this.normalizeId(parsed?.[fields[i]]);
          if (value) return value;
        }
      } catch {
        return null;
      }
      return null;
    },
    mergePurchaseOrderWithRecord(apiData) {
      const po = apiData || {};
      const row = this.record || {};
      return {
        ...po,
        orderCode: po.orderCode || row.orderCode,
        recordId: po.recordId || row.recordId,
        external_id: po.external_id ?? row.external_id ?? po.externalId ?? row.externalId,
        externalId: po.externalId ?? row.externalId ?? po.external_id ?? row.external_id,
        internalId: po.internalId ?? row.internalId ?? po.internal_id ?? row.internal_id,
        internal_id: po.internal_id ?? row.internal_id ?? po.internalId ?? row.internalId,
      };
    },
    close() {
      this.visible = false;
      this.$emit("close");
    },
    onEdit() {
      this.$emit("edit", this.record || this.purchaseOrder);
    },
    downloadQrCodes() {
      this.$notify({
        type: "info",
        title: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadQr"),
        text: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadQrPending"),
      });
    },
    async downloadReport() {
      const payload = this.purchaseOrder || this.record;
      this.$emit("download-report", payload);
      if (!payload || !this.$refs.reportPdfGenerator) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text: this.$t("memberDataSection.purchaseOrderDetail.reportDataMissing"),
        });
        return;
      }

      this.generatingReport = true;
      try {
        await this.$refs.reportPdfGenerator.generatePdf(payload, this.record);
        this.$notify({
          type: "success",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text: this.$t("memberDataSection.purchaseOrderDetail.downloadReportSuccess"),
        });
      } catch (err) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text:
            err?.message ||
            this.$t("memberDataSection.purchaseOrderDetail.downloadReportFailed"),
        });
      } finally {
        this.generatingReport = false;
      }
    },
    async onQualityControl() {
      this.$emit("quality-control", this.purchaseOrder || this.record);
      await this.loadGradingOptions();
      this.qualityGradingVisible = true;
    },
    async loadGradingOptions() {
      try {
        const { data } = await this.$http.get("/coffee/greencoffee/options");
        const payload = data?.data || data || {};
        this.gradingQualityScores = payload.greenCoffeeQuality || [];
        this.gradingLabels = payload.labels || [];
        const grades = payload.gradeOptions || [];
        this.gradingGradeOptions = grades.length
          ? grades
          : [...DEFAULT_GRADING_GRADE_OPTIONS];
      } catch (err) {
        console.log("Purchase order grading options fetch error", err);
        if (!this.gradingGradeOptions.length) {
          this.gradingGradeOptions = [...DEFAULT_GRADING_GRADE_OPTIONS];
        }
      }
    },
    onQualityGradingFinished(lots) {
      this.qualityGradingLots = lots || [];
      if (this.purchaseOrder) {
        this.purchaseOrder.qualityGradingLots = this.qualityGradingLots;
      }
      this.$notify({
        type: "success",
        title: this.$t("memberDataSection.greenCoffee.finishGrading"),
        text: this.$t("memberDataSection.purchaseOrderDetail.qualityGradingFinished"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.po-detail-card {
  border-radius: 4px;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
}

.po-detail-dialog__body {
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  background: #fff;
}

.po-detail-section + .po-detail-section {
  padding-top: 8px;
}

.po-detail-section__title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
  font-size: 16px;
  line-height: 1.35;
}

.po-detail-section__edit-btn {
  box-shadow: none;
}

.po-detail-plain-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
}

.po-detail-grid-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 16px;
}

.po-detail-grid-row--spaced {
  margin-top: 16px;
}

.po-detail-grid-cell {
  min-width: 0;
}

.po-detail-grid-cell--full {
  grid-column: 1 / -1;
  max-width: calc(33.333% - 14px);
}

.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.35;
  margin-bottom: 2px;
}

.field-value {
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  word-break: break-word;
}

.po-detail-media-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
}

.po-detail-photo,
.po-detail-photo-placeholder {
  flex-shrink: 0;
  border: 1px solid #e0e0e0;
}

.po-detail-photo-placeholder {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.po-detail-section-divider {
  border-color: #e0e0e0;
}

.po-detail-qr-row {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 20px;
}

.po-detail-qr-group {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
}

.po-detail-qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  width: max-content;
}

.po-detail-qr-download-btn {
  flex-shrink: 0;
  align-self: center;
}

.po-detail-qr-placeholder {
  width: 108px;
  height: 108px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.po-detail-qr-placeholder ::v-deep img,
.po-detail-qr-placeholder ::v-deep canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.po-detail-qr-id-line {
  margin: 0;
  text-align: center;
}

.po-detail-qr-id-line__text {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.35;
  white-space: nowrap;
}

.po-detail-qr-id-missing {
  color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
}

.po-detail-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.po-detail-footer {
  background: #fff;
}

@media (max-width: 600px) {
  .po-detail-grid-row {
    grid-template-columns: 1fr;
  }

  .po-detail-grid-cell--full {
    max-width: 100%;
  }

  .po-detail-qr-row {
    flex-wrap: wrap;
  }

  .po-detail-qr-group {
    gap: 12px;
  }
}
</style>
